import { useEffect, useRef, useState } from 'react';
import { authService, AuthRequestError } from '../../services/auth';
import { i18nService } from '../../services/i18n';
import {
  emitLoginEvent,
  loginErrorKey,
  isLoginPhoneValid,
  loginPhoneE164,
  parseLoginPhone,
  type LoginPhoneRegion,
  type LoginMethod,
} from './loginPolicy';
import { useSmsCooldown } from './useSmsCooldown';

export type LoginMode = LoginMethod | '2fa' | 'wechat_phone_bind';
export type LoginField = 'account' | 'password' | 'phone' | 'code' | 'captcha';
type FieldErrors = Partial<Record<LoginField, string>>;
interface LoginFormOptions {
  mode: LoginMode;
  agreed: boolean;
  phoneEnabled: boolean;
  requireAgreement: () => boolean;
  onMode: (mode: LoginMode) => void;
  onSuccess: () => void;
  onNotice: (message: string) => void;
}

/** 登录表单业务：本地校验、提交互斥、短信冷却、二次验证；视觉与扫码生命周期独立。 */
export function useLoginForm(options: LoginFormOptions) {
  const [account, setAccount] = useState('');
  const [password, setPassword] = useState('');
  const [loginRegion, setLoginRegion] = useState<LoginPhoneRegion>('cn');
  const [bindRegion, setBindRegion] = useState<LoginPhoneRegion>('cn');
  // 两个流程分别保留号码与区号，返回普通登录时不会串用绑定身份。
  const isBinding = options.mode === 'wechat_phone_bind';
  const phoneRegion = isBinding ? bindRegion : loginRegion;
  const setPhoneRegion = isBinding ? setBindRegion : setLoginRegion;
  const [phone, setPhone] = useState('');
  const [bindPhone, setBindPhone] = useState('');
  const [code, setCode] = useState('');
  // 展开状态只属于本次页面里发送成功的号码，不把共享冷却时间当作发送成功凭据。
  const [codeRecipients, setCodeRecipients] = useState({ login: '', bind: '' });
  const smsScene = isBinding ? 'bind' : 'login';
  const activePhone = isBinding ? bindPhone : phone;
  const codeRequested =
    Boolean(activePhone) &&
    codeRecipients[smsScene] === loginPhoneE164(activePhone, phoneRegion);
  const [captcha, setCaptcha] = useState<{ id: string; image: string } | null>(
    null,
  );
  const [captchaCode, setCaptchaCode] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [sending, setSending] = useState(false);
  const loginCooldown = useSmsCooldown('login');
  const bindCooldown = useSmsCooldown('bind');
  const cooldown =
    options.mode === 'wechat_phone_bind' ? bindCooldown : loginCooldown;
  const pending2fa = useRef<{
    username: string;
    password: string;
    ticket?: string | null;
  } | null>(null);
  const wechatToken = useRef<string | null>(null);
  const submitLock = useRef(false);
  const smsLock = useRef(false);
  const mounted = useRef(false);
  const bindingRequest = useRef<AbortController | null>(null);
  const captchaSequence = useRef(0);
  const t = (key: string) => i18nService.t(key);
  /** 优先翻译稳定错误码，保留平台锁定时长、禁用账号等具体业务说明。 */
  const failureMessage = (failure: unknown) => {
    const key =
      failure instanceof AuthRequestError
        ? loginErrorKey(failure.code || '')
        : null;
    return key
      ? t(key)
      : failure instanceof Error
        ? failure.message
        : t('connectionFailed');
  };

  /** 图形验证码仍按服务端要求展示，序列号防止刷新较慢的旧响应覆盖新图。 */
  const loadCaptcha = async () => {
    const sequence = ++captchaSequence.current;
    try {
      const result = await authService.fetchCaptcha();
      if (!mounted.current || sequence !== captchaSequence.current) return;
      setCaptcha(
        result ? { id: result.captchaId, image: result.captchaData } : null,
      );
      setCaptchaCode('');
    } catch {
      if (mounted.current) setError(t('connectionFailed'));
    }
  };
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      bindingRequest.current?.abort();
      captchaSequence.current++;
    };
  }, []);
  useEffect(() => {
    if (options.mode === 'password') void loadCaptcha();
  }, [options.mode]);

  /** 切换保留账号和手机号，清除敏感凭据与上次校验错误。 */
  const reset = () => {
    bindingRequest.current?.abort();
    pending2fa.current = null;
    wechatToken.current = null;
    setPassword('');
    setCode('');
    setCaptchaCode('');
    setErrors({});
    setError(null);
  };
  const clearError = (field: LoginField) => {
    setErrors((previous) => ({ ...previous, [field]: undefined }));
    setError(null);
  };
  /** 更换身份时清空验证码，避免使用旧号码的验证码提交新号码。 */
  const updatePhone = (input: string) => {
    const parsed = parseLoginPhone(input, phoneRegion);
    setPhoneRegion(parsed.region);
    if (options.mode === 'wechat_phone_bind') setBindPhone(parsed.local);
    else setPhone(parsed.local);
    setCode('');
    setErrors({});
    setError(null);
  };
  /** 显式切换区号后清空当前号码，防止错误区号与旧号码组合发送短信。 */
  const changePhoneRegion = (region: LoginPhoneRegion) => {
    setPhoneRegion(region);
    if (options.mode === 'wechat_phone_bind') setBindPhone('');
    else setPhone('');
    setCode('');
    setErrors({});
  };

  /** 微信要求补绑手机号时只保存本次流程令牌；离开后立即销毁。 */
  const beginBinding = (token: string) => {
    reset();
    setCodeRecipients((previous) => ({ ...previous, bind: '' }));
    wechatToken.current = token;
    options.onMode('wechat_phone_bind');
  };

  /** 统一发送入口，前端互斥仅用于交互；频率和注册资格仍由 Platform 校验。 */
  const sendSms = async () => {
    if (
      smsLock.current ||
      submitLock.current ||
      cooldown.seconds > 0 ||
      !options.phoneEnabled
    )
      return;
    if (!options.requireAgreement()) return;
    const value = options.mode === 'wechat_phone_bind' ? bindPhone : phone;
    if (!isLoginPhoneValid(value, phoneRegion)) {
      setErrors({ phone: t('authInvalidPhone') });
      return;
    }
    smsLock.current = true;
    setSending(true);
    setError(null);
    setErrors({});
    try {
      if (options.mode === 'wechat_phone_bind')
        await authService.sendSmsBindCode(loginPhoneE164(value, phoneRegion));
      else
        await authService.sendLoginOrRegisterSms(
          loginPhoneE164(value, phoneRegion),
        );
      if (!mounted.current) return;
      setCodeRecipients((previous) => ({
        ...previous,
        [smsScene]: loginPhoneE164(value, phoneRegion),
      }));
      cooldown.start();
      emitLoginEvent('sms_send', { result: 'success' });
      if (mounted.current) options.onNotice(t('authSmsCodeSent'));
    } catch (failure) {
      emitLoginEvent('sms_send', {
        result: 'failed',
        error_code:
          failure instanceof AuthRequestError
            ? failure.code || String(failure.status)
            : 'network',
      });
      if (!mounted.current) return;
      setErrors({ code: failureMessage(failure) });
      if (failure instanceof AuthRequestError && failure.status === 429)
        cooldown.start(failure.retryAfterSeconds || 60);
    } finally {
      smsLock.current = false;
      if (mounted.current) setSending(false);
    }
  };

  /** 同一页面只允许一个认证请求，按钮与回车提交共用这把锁。 */
  const submit = async () => {
    if (submitLock.current || smsLock.current || (options.mode === 'wechat' || options.mode === 'appqr'))
      return;
    if (!options.requireAgreement()) return;
    if (
      (options.mode === 'phone' || options.mode === 'wechat_phone_bind') &&
      !options.phoneEnabled
    )
      return;
    const validation: FieldErrors = {};
    if (options.mode === 'password') {
      if (!account.trim()) validation.account = t('loginAccountRequired');
      if (!password) validation.password = t('loginPasswordRequired');
      if (captcha && !captchaCode.trim())
        validation.captcha = t('authCaptchaRequired');
    } else if (options.mode === '2fa') {
      if (!code.trim()) validation.code = t('authTwoFactorCodeRequired');
    } else {
      if (
        !isLoginPhoneValid(
          options.mode === 'phone' ? phone : bindPhone,
          phoneRegion,
        )
      )
        validation.phone = t('authInvalidPhone');
      if (!/^\d{6}$/.test(code)) validation.code = t('loginCodeInvalid');
    }
    setErrors(validation);
    setError(null);
    if (Object.keys(validation).length) return;
    submitLock.current = true;
    setSubmitting(true);
    emitLoginEvent('login_submit', { method: options.mode });
    try {
      if (options.mode === 'password') {
        const result = await authService.login({
          username: account,
          password,
          captchaId: captcha?.id,
          captchaCode: captchaCode || undefined,
        });
        if (result.status === 'requires_2fa') {
          pending2fa.current = result;
          setPassword('');
          setCode('');
          options.onMode('2fa');
          return;
        }
      } else if (options.mode === '2fa') {
        if (!pending2fa.current) throw new Error(t('authBackToLogin'));
        await authService.loginWith2FA({ ...pending2fa.current, code });
        pending2fa.current = null;
      } else if (options.mode === 'phone') {
        await authService.loginOrRegisterWithPhone({
          phone: loginPhoneE164(phone, phoneRegion),
          code,
        });
      } else {
        if (!wechatToken.current) throw new Error(t('authWeChatQrExpired'));
        const request = new AbortController();
        bindingRequest.current = request;
        await authService.registerWeChatWithPhone(
          {
            wechatToken: wechatToken.current,
            phone: loginPhoneE164(bindPhone, phoneRegion),
            code,
          },
          request.signal,
        );
        if (request.signal.aborted) return;
        wechatToken.current = null;
      }
      emitLoginEvent('login_result', { method: options.mode, success: true });
      options.onSuccess();
    } catch (failure) {
      if (
        !mounted.current ||
        (failure instanceof DOMException && failure.name === 'AbortError')
      )
        return;
      const message = failureMessage(failure);
      emitLoginEvent('login_result', {
        method: options.mode,
        success: false,
        error_code:
          failure instanceof AuthRequestError
            ? failure.code || String(failure.status)
            : 'request_failed',
      });
      if (options.mode === 'password') {
        // 保留锁定、频率和图形验证码等服务端提示；身份错误统一展示，避免枚举账号。
        const invalidCredentials =
          /账号不存在|用户不存在|密码错误|password.*incorrect|invalid.*credentials|user.*not.*exist/i.test(
            message,
          );
        setErrors({
          password: invalidCredentials ? t('loginPasswordInvalid') : message,
        });
        if (captcha) void loadCaptcha();
      } else setErrors({ code: message });
    } finally {
      submitLock.current = false;
      if (mounted.current) setSubmitting(false);
    }
  };


  return {
    codeRequested,
    phoneRegion,
    updatePhone,
    changePhoneRegion,
    account,
    setAccount,
    password,
    setPassword,
    phone,
    setPhone,
    bindPhone,
    setBindPhone,
    code,
    setCode,
    captcha,
    captchaCode,
    setCaptchaCode,
    errors,
    error,
    submitting,
    sending,
    cooldown,
    loadCaptcha,
    clearError,
    reset,
    beginBinding,
    sendSms,
    submit,
  };
}
